<template>
  <Teleport to="body">
    <vue-final-modal
      v-model="showModal"
      classes="modal-container"
      content-class="modal-content main-modal plan-day-modal"
      overlay-class="vfm__overlay"
      :click-to-close="true"
      :lock-scroll="true"
      :hide-overlay="false"
      :esc-to-close="true"
      @click:overlay="closeModal"
      @click="handleOutsideClick"
    >
      <div class="p-3" @click.stop>
        <div class="card">
          <div class="card-body">
            <div class="row py-2">
              <div class="col-md-2">
                日付:
              </div>
              <div class="col-md-6">
                <p v-if="targetDay" class="mb-0">{{dayjs(targetDay).format('YYYY年MM月DD日')}}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-md-12 text-right">
            <a href="#" @click.prevent="closeModal">キャンセル</a>
            <span class="mx-1"></span>
            <button type="button" class="action-button btn btn-secondary btn-rounded" @click="showCopyModal"
                    :disabled="isProcessing">
              <i class="fa fa-copy"></i>&nbsp;コピーする(1日分)
            </button>
            <span class="mx-1"></span>
            <span class="mx-1"></span>
            <button type="button" class="action-button btn btn-danger btn-rounded" @click="destroy"
                    :disabled="isProcessing">
              <i class="fa fa-trash"></i>&nbsp;削除する(１日分)
            </button>
          </div>
        </div>
      </div>
    </vue-final-modal>

    <vue-final-modal
      v-model="showCopyModalValue"
      classes="modal-container"
      content-class="modal-content copy-modal"
      overlay-class="vfm__overlay"
      :click-to-close="true"
      :lock-scroll="true"
      :hide-overlay="false"
      :esc-to-close="true"
      @click:overlay="closeCopyModal"
      @click="handleCopyOutsideClick"
    >
      <div class="content p-3" @click.stop>
        <div class="card">
          <div class="card-body">
            <div class="row">
              <div class="col-md-9">
                <div class="mb-3">
                  <label class="form-label">コピー先</label>
                  <input type="date" class="form-control" v-model="copyDest">
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-md-12 text-end">
            <a href="#" @click.prevent="closeCopyModal" class="btn btn-link">キャンセル</a>
            <span class="mx-1"></span>
            <button type="button" class="btn btn-success rounded-pill"
                    @click="copy"
                    :disabled="isProcessing || !copyDest">
              <i class="fa fa-edit"></i>&nbsp;コピーする
            </button>
          </div>
        </div>
      </div>
    </vue-final-modal>

    <BToast
      v-model="showToast"
      :title="toastTitle"
      :variant="toastVariant"
      toaster="top-center"
      :auto-hide="true"
      :delay="2000"
    >
      {{ toastMessage }}
    </BToast>
  </Teleport>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import dayjs from 'dayjs'
import { VueFinalModal } from 'vue-final-modal'
import { BToast } from 'bootstrap-vue-next'
import api from '../libs/Api'
import Constants from '../constants'
import utils from '../libs/Utils'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  officeId: {
    type: [String, Number],
    default: null
  },
  userId: {
    type: [String, Number],
    default: null
  },
  targetDay: {
    type: [String, Date, Object],
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'change', 'cancel', 'edit'])

const showModal = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value)
  }
})

const isProcessing = ref(false)
const showCopyModalValue = ref(false)
const copyDest = ref(null)

const showToast = ref(false)
const toastMessage = ref('')
const toastTitle = ref('')
const toastVariant = ref('success')

watch(() => props.modelValue, (newVal) => {
}, { immediate: true })

watch(() => props.targetDay, (newVal) => {
}, { immediate: true })

const showSuccessToast = (message) => {
  toastMessage.value = message
  toastTitle.value = '成功'
  toastVariant.value = 'success'
  showToast.value = true
}

const showErrorToast = (message) => {
  toastMessage.value = message
  toastTitle.value = 'エラー'
  toastVariant.value = 'danger'
  showToast.value = true
}

const closeModal = () => {
  showModal.value = false
  emit('cancel')
}

const closeCopyModal = () => {
  showCopyModalValue.value = false
}

const showCopyModal = () => {
  copyDest.value = utils.formatDay(props.targetDay)
  showCopyModalValue.value = true
}

const copy = async () => {
  if (isProcessing.value || !copyDest.value) return

  try {
    isProcessing.value = true
    const dest = dayjs(copyDest.value, 'YYYY-MM-DD')
    await api.copyPlan(
      props.officeId,
      props.userId,
      dayjs(props.targetDay).format('YYYYMMDD'),
      dest.format('YYYYMMDD'),
      Constants.PLAN_COPY_TYPE_DAY
    )

    showSuccessToast('コピーしました。')
    showCopyModalValue.value = false
    showModal.value = false
    emit('change', null)
  } catch (error) {
    console.error(error)
    showErrorToast('エラーが発生しました。')
  } finally {
    isProcessing.value = false
  }
}

const destroy = async () => {
  if (isProcessing.value) return

  if (confirm('１日分のプランを削除します。未実施のステータスのプランが削除対象です。\n\nこの操作は取り消せません。削除してもよろしいですか？')) {
    try {
      isProcessing.value = true
      const targetDate = dayjs(props.targetDay).format('YYYYMMDD')
      await api.deleteRangePlan(props.officeId, props.userId, targetDate, targetDate)

      showSuccessToast('削除しました。')
      showModal.value = false
      emit('change', null)
    } catch (error) {
      console.error(error)
      showErrorToast('エラーが発生しました。')
    } finally {
      isProcessing.value = false
    }
  }
}

const handleOutsideClick = (event) => {
  if (event.target.classList.contains('vfm__overlay')) {
    closeModal()
  }
}

const handleCopyOutsideClick = (event) => {
  if (event.target.classList.contains('vfm__overlay')) {
    closeCopyModal()
  }
}
</script>

<style>
.content {
  width: 100%;
}

.main-modal {
  width: 650px;
  height: 270px;
}

.btn-link {
  text-decoration: none;
  color: #6c757d;
}

.btn-link:hover {
  color: #4b5156;
  text-decoration: underline;
}

input[type="date"].form-control {
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  border-radius: 0.25rem;
  border: 1px solid #ced4da;
}

.plan-day-modal {
  width: 650px !important;
}
</style>
